import React, { useMemo, useRef, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface AuditRecord {
  category: string;
  totalNC: number | null;
  completionNC: number | null;
  pending: number | null;
  completionPercent: number | null;
  currentScore: number | null;
  maxScore: number;
}

interface AuditReportChartProps {
  externalRecords: AuditRecord[];
  internalRecords: AuditRecord[];
  /** Called after chart renders — receives a function that returns a white-bg PNG data URL */
  onCanvasReady?: (getImage: () => string) => void;
}

const CAT_COLORS: Record<string, {
  ext: { bg: string; border: string };
  int: { bg: string; border: string };
}> = {
  A: {
    ext: { bg: 'rgba(220,38,38,0.85)', border: 'rgba(185,28,28,1)' },
    int: { bg: 'rgba(251,113,133,0.85)', border: 'rgba(244,63,94,1)' },
  },
  B: {
    // ext: { bg: 'rgba(245,158,11,0.88)',  border: 'rgba(217,119,6,1)'   },
    ext: { bg: 'rgba(251, 247, 12, 0.92)', border: 'rgba(222, 214, 4, 0.94)' },
    int: { bg: 'rgba(248, 248, 116, 1)', border: 'rgba(235, 235, 15, 0.97)' },
  },
  C: {
    ext: { bg: 'rgba(22,163,74,0.85)', border: 'rgba(21,128,61,1)' },
    int: { bg: 'rgba(134,239,172,0.85)', border: 'rgba(74,222,128,1)' },
  },
};

// ─── Bar value label plugin ────────────────────────────────────────────────────
// Uses afterDatasetsDraw (not afterRender) — only fires when chart data changes,
// NOT on every tooltip hover, so zero flicker.
const barLabelPlugin = {
  id: 'auditBarLabels',
  afterDatasetsDraw(chart: any) {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset: any, di: number) => {
      const meta = chart.getDatasetMeta(di);
      if (meta.hidden || !meta?.data?.length) return;
      if (dataset.label === '_gap') return;

      meta.data.forEach((bar: any, idx: number) => {
        const val: number = dataset.data[idx];
        if (!val) return;

        const x = bar.x;
        const top = bar.y;
        const scoreLabel = val.toFixed(1);
        const pct: number | null = dataset._pcts?.[idx] ?? null;
        const pctLabel = pct != null ? `(${pct.toFixed(0)}%)` : '';

        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.font = 'bold 10px Arial';
        ctx.fillStyle = '#1f2937';

        if (pctLabel) {
          ctx.fillText(pctLabel, x, top - 2);
          ctx.fillText(scoreLabel, x, top - 13);
        } else {
          ctx.fillText(scoreLabel, x, top - 3);
        }
        ctx.restore();
      });
    });
  },
};

// ─── Component ─────────────────────────────────────────────────────────────────
const AuditReportChart: React.FC<AuditReportChartProps> = ({
  externalRecords,
  internalRecords,
  onCanvasReady,
}) => {
  const chartRef = useRef<any>(null);
  const categories = ['A', 'B', 'C'] as const;

  const extData = useMemo(() =>
    categories.map(cat => {
      const r = externalRecords.find(x => x.category === cat);
      return { score: r?.currentScore ?? null, maxScore: r?.maxScore ?? 0, pct: r?.completionPercent ?? null };
    }),
    [externalRecords]);

  const intData = useMemo(() =>
    categories.map(cat => {
      const r = internalRecords.find(x => x.category === cat);
      return { score: r?.currentScore ?? null, maxScore: r?.maxScore ?? 0, pct: r?.completionPercent ?? null };
    }),
    [internalRecords]);

  const yMax = useMemo(() => {
    const all = [...extData, ...intData].map(d => d.maxScore);
    return Math.ceil(Math.max(...all, 1) * 1.40);
  }, [extData, intData]);

  // ── Notify parent with a getImage() function after chart renders ────────────
  useEffect(() => {
    if (!onCanvasReady) return;
    // Use a short timeout so Chart.js finishes drawing before we capture
    const t = setTimeout(() => {
      onCanvasReady(() => {
        const chart = chartRef.current;
        if (!chart) return '';
        const src = chart.canvas as HTMLCanvasElement;
        const off = document.createElement('canvas');
        off.width = src.width; off.height = src.height;
        const ctx = off.getContext('2d')!;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, off.width, off.height);
        ctx.drawImage(src, 0, 0);
        return off.toDataURL('image/png');
      });
    }, 300);
    return () => clearTimeout(t);
  }, [onCanvasReady, externalRecords, internalRecords]);

  // ── Download — white background so canvas isn't transparent ─────────────────
  const handleDownload = () => {
    const chart = chartRef.current;
    if (!chart) return;
    const src = chart.canvas as HTMLCanvasElement;
    const off = document.createElement('canvas');
    off.width = src.width;
    off.height = src.height;
    const octx = off.getContext('2d')!;
    octx.fillStyle = '#ffffff';
    octx.fillRect(0, 0, off.width, off.height);
    octx.drawImage(src, 0, 0);
    const link = document.createElement('a');
    link.download = 'audit-report-chart.png';
    link.href = off.toDataURL('image/png');
    link.click();
  };

  // Per-category colors as flat arrays indexed by category position (0=A,1=B,2=C)
  const extBg = categories.map(c => CAT_COLORS[c].ext.bg);
  const extBorder = categories.map(c => CAT_COLORS[c].ext.border);
  const intBg = categories.map(c => CAT_COLORS[c].int.bg);
  const intBorder = categories.map(c => CAT_COLORS[c].int.border);

  const chartData = useMemo(() => ({
    labels: categories.map(c => `Category ${c}`),
    datasets: [
      {
        label: 'External Score',
        data: extData.map(d => d.score ?? 0),
        _pcts: extData.map(d => d.pct),
        backgroundColor: extBg,
        borderColor: extBorder,
        borderWidth: 1.5,
        borderRadius: 4,
        categoryPercentage: 0.45,  // tighter group → bars closer together
        barPercentage: 1.0,
        order: 1,
      },
      {
        // Invisible spacer — creates a small visual gap between Ext and Int
        label: '_gap',
        data: categories.map(() => 0),
        backgroundColor: 'transparent',
        borderWidth: 0,
        categoryPercentage: 0.45,
        barPercentage: 0.06,       // very narrow = minimal gap
        order: 2,
      },
      {
        label: 'Internal Score',
        data: intData.map(d => d.score ?? 0),
        _pcts: intData.map(d => d.pct),
        backgroundColor: intBg,
        borderColor: intBorder,
        borderWidth: 1.5,
        borderRadius: 4,
        categoryPercentage: 0.45,
        barPercentage: 1.0,
        order: 3,
      },
    ],
  }), [extData, intData]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 36, left: 8, right: 8, bottom: 8 } },
    plugins: {
      // ── Built-in legend at top — stable, no flicker, included in download ──
      legend: {
        display: true,
        position: 'top' as const,
        align: 'center' as const,
        labels: {
          font: { size: 12 },
          padding: 16,
          usePointStyle: true,
          pointStyle: 'rect' as const,
          // Build 6 entries (one per cat×side) from the per-bar color arrays.
          // Chart.js by default makes one entry per dataset; we override to show
          // per-category swatches instead.
          generateLabels: () => [
            { text: 'Cat A — External', fillStyle: extBg[0], strokeStyle: extBorder[0], lineWidth: 1.5, hidden: false, datasetIndex: 0 },
            { text: 'Cat A — Internal', fillStyle: intBg[0], strokeStyle: intBorder[0], lineWidth: 1.5, hidden: false, datasetIndex: 2 },
            { text: 'Cat B — External', fillStyle: extBg[1], strokeStyle: extBorder[1], lineWidth: 1.5, hidden: false, datasetIndex: 0 },
            { text: 'Cat B — Internal', fillStyle: intBg[1], strokeStyle: intBorder[1], lineWidth: 1.5, hidden: false, datasetIndex: 2 },
            { text: 'Cat C — External', fillStyle: extBg[2], strokeStyle: extBorder[2], lineWidth: 1.5, hidden: false, datasetIndex: 0 },
            { text: 'Cat C — Internal', fillStyle: intBg[2], strokeStyle: intBorder[2], lineWidth: 1.5, hidden: false, datasetIndex: 2 },
          ],
          // Clicking a legend item does nothing (colors are per-bar, not per-dataset)
          onClick: () => { },
        },
      },
      title: {
        display: true,
        text: 'Audit Report — Score by Category (External & Internal)',
        font: { size: 14, weight: 'bold' as const },
        padding: { top: 4, bottom: 10 },
        color: '#1f2937',
      },
      tooltip: {
        backgroundColor: 'rgba(17,24,39,0.92)',
        padding: 12,
        cornerRadius: 8,
        filter: (item: any) => item.dataset.label !== '_gap',
        callbacks: {
          title: (items: any[]) => `Category ${categories[items[0]?.dataIndex]}`,
          label: (ctx: any) => {
            const idx = ctx.dataIndex;
            if (ctx.dataset.label === 'External Score') {
              const d = extData[idx];
              return d.pct != null
                ? `External: ${(d.score ?? 0).toFixed(2)} / ${d.maxScore} (${d.pct.toFixed(1)}%)`
                : `External: ${(d.score ?? 0).toFixed(2)} / ${d.maxScore}`;
            }
            if (ctx.dataset.label === 'Internal Score') {
              const d = intData[idx];
              return d.pct != null
                ? `Internal: ${(d.score ?? 0).toFixed(2)} / ${d.maxScore} (${d.pct.toFixed(1)}%)`
                : `Internal: ${(d.score ?? 0).toFixed(2)} / ${d.maxScore}`;
            }
            return '';
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 13, weight: 'bold' as const }, color: '#374151' },
      },
      y: {
        beginAtZero: true,
        max: yMax,
        grid: { color: 'rgba(0,0,0,0.06)' },
        ticks: { font: { size: 11 }, color: '#6b7280' },
        title: {
          display: true,
          text: 'Score',
          font: { size: 12, weight: 'bold' as const },
          color: '#374151',
        },
      },
    },
  }), [extData, intData, yMax]);

  return (
    <div className="w-full mt-3 bg-white p-4 rounded-lg border border-gray-200">
      {/* Download button */}
      <div className="flex justify-end mb-2">
        <button
          onClick={handleDownload}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[#00A8A5] hover:bg-[#008A87] rounded-lg transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
          Download Chart
        </button>
      </div>

      <div className="w-full h-[440px]">
        <Bar
          ref={chartRef}
          data={chartData}
          options={chartOptions as any}
          plugins={[barLabelPlugin]}
        />
      </div>
    </div>
  );
};

export default AuditReportChart;